/**
 * Subject Trie · dispatch routing primitive · T2203 (h3 L1109).
 *
 * Spec invariant (file 05 §L1109):
 *   "Single canonical delivery per event, regardless of how many trie nodes
 *    match." If a hook acquired both the wildcard `morfi.market.*.trade`
 *    AND the per-entity `morfi.market.M1.trade`, its dispatchEvent would be
 *    registered on BOTH trie nodes — a naive walk would fire twice per
 *    event. APPEND/PREPEND updaters would then double-write.
 *
 * Per-subject `lastSeq` dedup (NOT trie-root) prevents the double-fire:
 *   when the walk visits a Subject whose `seq <= subject.lastSeq`, the
 *   visit is skipped. The first matching node bumps `lastSeq` to `seq`,
 *   so subsequent matching nodes in the SAME walk see `seq <= lastSeq`
 *   and short-circuit. Observers are notified exactly once per
 *   `{subject, seq}` pair.
 *
 * Cost: O(depth(subject)) per event — at most 5 visits for
 * `morfi.market.M1.trade`, regardless of how many observers are
 * registered on the matched Subject.
 */

/** Token-stream walker callback — invoked at every terminator the walk hits. */
type SubjectVisitor = (subject: Subject) => void;

/**
 * T2400 · h3 L1060 — Wildcard-cover detection primitive.
 *
 * Returns true iff `pattern` (which may contain `*` / `>`) would match the
 * concrete `requested` subject. Used by `isCoveredByOpen` to short-circuit
 * SUB issuance when an already-open wildcard subsumes the request.
 *
 *   subjectMatches('morfi.market.*.trade', 'morfi.market.M1.trade')      → true
 *   subjectMatches('morfi.market.*.trade', 'morfi.gift.M1.trade')        → false
 *   subjectMatches('morfi.market.>',       'morfi.market.M1.trade')      → true
 *   subjectMatches('morfi.market.M1.>',    'morfi.market.M1.trade')      → true
 *   subjectMatches('morfi.market.M1.>',    'morfi.market.M2.trade')      → false
 */
export function subjectMatches(pattern: string, requested: string): boolean {
  const pTokens = pattern.split('.');
  const rTokens = requested.split('.');
  let i = 0;
  while (i < pTokens.length) {
    const p = pTokens[i];
    if (p === '>') return true; // `>` swallows the remainder regardless of length
    if (i >= rTokens.length) return false;
    if (p !== '*' && p !== rTokens[i]) return false;
    i++;
  }
  return i === rTokens.length;
}

export interface Observer {
  /** Invoked exactly once per matched `{subject, seq}` pair. */
  dispatch(subjectStr: string, ev: unknown): void;
}

export interface QueryObserverLifecycle {
  observerCount: number;
  lastDeregisteredAt: number;
  lastDeregisteredSeq: number;
}

/**
 * @invariant T18104 · lastSeq is per-subject; there is NO registry-root
 *   lastSeq. JetStream sequence numbers are per-subject-stream (NATS JS
 *   SDK v2.x docs) and cannot be totally ordered across subjects. A
 *   "root-level dedup" phrasing in spec L1110-L1111 refers to the
 *   registry's TOTAL responsibility for dedup, not a global counter. The
 *   total dedup is achieved by walking every matching trie node and
 *   gating on each node's per-subject `lastSeq`. A complementary
 *   root-level `Map<subject, seq>` is maintained in `walk()` (T15111) as a
 *   belt-and-braces early-exit guard BEFORE the trie descent.
 */
export interface Subject {
  /** Refcount of distinct acquire() calls pointing at this subject. NOT a NatsSubscription. */
  interestCount: number;
  /** Dedup guard PER SUBJECT (not trie-root). Bumped on first matching walk visit. */
  lastSeq: number;
  /** Per-call-site observers; NOT deduped by queryKey. */
  observers: Set<Observer>;
  /**
   * Per-query observer lifecycle. Different queries can share one wire
   * subject, so each query needs its own release sequence to detect events
   * that arrived while that query alone was unmounted.
   */
  queryObservers: Map<string, QueryObserverLifecycle>;
  /** Set after inactive lifecycle history is trimmed to its bounded cap. */
  queryObserverHistoryEvicted: boolean;
}

export interface TrieNode {
  /** Exact-token children: token → child. */
  exact: Map<string, TrieNode>;
  /** Single-token '*' wildcard child. */
  star: TrieNode | null;
  /** '>' multi-token terminator at this level — matches anything below. */
  rest: Subject | null;
  /** Exact terminator at this level — the subject ENDS here. */
  subject: Subject | null;
}

export function createTrieNode(): TrieNode {
  return {
    exact: new Map(),
    star: null,
    rest: null,
    subject: null,
  };
}

export function createSubject(): Subject {
  return {
    interestCount: 0,
    lastSeq: 0,
    observers: new Set(),
    queryObservers: new Map(),
    queryObserverHistoryEvicted: false,
  };
}

/**
 * Insert a pattern into the trie. Tokens may include `*` (single) and `>`
 * (rest). Returns the Subject created or reused at the terminator.
 */
export function insertPattern(root: TrieNode, pattern: string): Subject {
  const tokens = pattern.split('.');
  let node = root;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token === undefined) {
      // unreachable — split returns at least one entry — but tsc with
      // noUncheckedIndexedAccess requires the guard.
      break;
    }
    const isLast = i === tokens.length - 1;
    if (token === '>') {
      if (node.rest === null) node.rest = createSubject();
      return node.rest;
    }
    if (token === '*') {
      if (node.star === null) node.star = createTrieNode();
      node = node.star;
    } else {
      let child = node.exact.get(token);
      if (child === undefined) {
        child = createTrieNode();
        node.exact.set(token, child);
      }
      node = child;
    }
    if (isLast) {
      if (node.subject === null) node.subject = createSubject();
      return node.subject;
    }
  }
  // Pattern was empty — treat as a self-subject at root.
  if (root.subject === null) root.subject = createSubject();
  return root.subject;
}

/**
 * Walk every matching Subject for `subjectStr`. Visitor is called once per
 * matched terminator — exact terminators AND `>` rest terminators along the
 * walk path. Order is exact-first (most-specific), then star, then rest.
 *
 * Cost: O(depth × fanout-of-wildcards). For typical morfi subjects with
 * at most one wildcard per level, this collapses to O(depth).
 */
function walk(
  node: TrieNode,
  tokens: readonly string[],
  index: number,
  visit: SubjectVisitor,
): void {
  // A `>` terminator at THIS level matches the rest of the stream. Visit
  // BEFORE descending — order is irrelevant for correctness but stable for
  // tests.
  if (node.rest !== null) visit(node.rest);

  if (index === tokens.length) {
    if (node.subject !== null) visit(node.subject);
    return;
  }

  const token = tokens[index];
  if (token === undefined) {
    if (node.subject !== null) visit(node.subject);
    return;
  }

  const exactChild = node.exact.get(token);
  if (exactChild !== undefined) walk(exactChild, tokens, index + 1, visit);
  if (node.star !== null) walk(node.star, tokens, index + 1, visit);
}

/**
 * T15111 · Root-level `{subject, seq}` dedup map keyed by trie root.
 *
 * Spec L1111: "Root-level dedup · on every inbound event the dispatcher
 * records `{event.subject, event.seq}` at trie root BEFORE walking." The
 * leaf-level `lastSeq` (T404) is correct for single-observer cases but
 * NOT equivalent when a wildcard + specific observer both match: without
 * a root-level early exit, both branches would BEGIN the walk before the
 * leaf check fires, doing duplicate work on the way. The root map is
 * belt-and-braces, NOT either/or — both checks run.
 *
 * Storage is a `WeakMap` so each trie root carries its own dedup table
 * and tear-down GC's the map automatically.
 */
const rootDedupBySubject = new WeakMap<TrieNode, Map<string, number>>();

/** Internal · exposed for tests to spy on / inspect. */
export function _getRootDedupMap(root: TrieNode): Map<string, number> {
  let m = rootDedupBySubject.get(root);
  if (m === undefined) {
    m = new Map();
    rootDedupBySubject.set(root, m);
  }
  return m;
}

/** Count-only read that does not create a dedup map for an untouched trie. */
export function getRootDedupSubjectCount(root: TrieNode): number {
  return rootDedupBySubject.get(root)?.size ?? 0;
}

/**
 * Dispatch an event to every observer on every matched Subject — but at
 * most once per Subject per `{subject, seq}` pair. Two-layer guard:
 *
 *   1. T15111 root-level `{subject, seq}` early exit BEFORE the walk.
 *   2. T404 per-Subject `lastSeq` guard inside the walk visitor.
 *
 * @param root          trie root.
 * @param subjectStr    full subject (e.g. `morfi.market.M1.trade`).
 * @param seq           monotonic NATS sequence — same `{subject, seq}` on
 *                      redelivery short-circuits the second visit.
 * @param ev            opaque payload — passed verbatim to observers.
 */
export function dispatch(root: TrieNode, subjectStr: string, seq: number, ev: unknown): boolean {
  // T15111 · root-level early-exit BEFORE any walk. The order is load-
  // bearing: a stale `_walkInner` call on duplicate seq fans out via every
  // matching branch in the trie before the leaf check fires.
  const rootMap = _getRootDedupMap(root);
  const lastSeen = rootMap.get(subjectStr);
  if (lastSeen !== undefined && seq <= lastSeen) return false;
  rootMap.set(subjectStr, seq);

  const tokens = subjectStr.split('.');
  let accepted = false;
  walk(root, tokens, 0, (subject) => {
    if (!dedupBySeq(subject, seq)) return;
    accepted = true;
    for (const observer of subject.observers.values()) {
      observer.dispatch(subjectStr, ev);
    }
  });
  return accepted;
}

/**
 * T404 · 3-line monotonic guard called inside the trie walk visitor BEFORE
 * fanning to observers. Guards against (a) JetStream at-least-once
 * redelivery, (b) wildcard-cover overlap (same event arriving via wildcard
 * AND specific subject). Lifetime tied to the release buffer — no
 * separate LRU cap.
 *
 * Returns `true` when the event is NEW (observers should fire); returns
 * `false` when `seq` is stale or equal to the last-seen value.
 */
export function dedupBySeq(subject: Subject, seq: number): boolean {
  if (seq <= subject.lastSeq) return false;
  subject.lastSeq = seq;
  return true;
}

// ===========================================================================
// T401 · h3 L1109 — createSubjectTrie factory + matchPattern helper.
//
// Factory wrapping the lower-level trie primitives so callers don't need to
// thread a TrieNode root through their code. Adds:
//
//   - register(pattern, observer) → unregister  · trie insert + observer-set
//                                                  attach with refcount.
//   - walk(literal, visit)                       · token-stream walker that
//                                                  visits every matching
//                                                  Subject (uses internal walk).
//   - matchPattern(literal)                      · returns the MOST-SPECIFIC
//                                                  registered pattern that
//                                                  matches `literal`, or null.
//   - subjectMatches(pattern, literal)           · pure helper, re-exported
//                                                  for ergonomics.
//
// Pattern specificity ordering (most → least specific):
//   1. Exact terminator (`morfi.market.M1.trade`)
//   2. Single-token wildcard chain (`morfi.market.*.trade`)
//   3. Rest wildcard (`morfi.market.>`)
//
// Tie-breaker between two single-token patterns at the same level is
// "more-exact-prefix wins" — `morfi.market.*.trade` beats `morfi.*.M1.trade`
// because the exact tokens appear earlier in the path. Two patterns with
// identical specificity at the SAME path are a register-time error.
// ===========================================================================

/**
 * Tracks the canonical pattern string for each terminator Subject so
 * `matchPattern` can return the registered string verbatim rather than
 * reconstructing it from the walk path.
 */
type PatternIndex = Map<Subject, string>;

/**
 * Specificity tier for a candidate match. Smaller = more specific.
 *   0 = exact terminator
 *   1 = single-token wildcard chain
 *   2 = rest-wildcard terminator
 */
type Tier = 0 | 1 | 2;

interface MatchCandidate {
  pattern: string;
  tier: Tier;
  /** Tie-breaker among same-tier matches: count of exact tokens consumed. */
  exactTokens: number;
}

function findBestMatch(
  node: TrieNode,
  tokens: readonly string[],
  index: number,
  exactTokens: number,
  patternIndex: PatternIndex,
  best: MatchCandidate | null,
): MatchCandidate | null {
  // `>` rest-wildcard at this level always matches.
  if (node.rest !== null) {
    const pattern = patternIndex.get(node.rest);
    if (pattern !== undefined) {
      const candidate: MatchCandidate = { pattern, tier: 2, exactTokens };
      best = takeMoreSpecific(best, candidate);
    }
  }

  if (index === tokens.length) {
    if (node.subject !== null) {
      const pattern = patternIndex.get(node.subject);
      if (pattern !== undefined) {
        // Tier is 0 (exact) iff no '*' was consumed on the walk.
        const isExact = !pattern.includes('*');
        const candidate: MatchCandidate = {
          pattern,
          tier: isExact ? 0 : 1,
          exactTokens,
        };
        best = takeMoreSpecific(best, candidate);
      }
    }
    return best;
  }

  const token = tokens[index];
  if (token === undefined) return best;

  const exactChild = node.exact.get(token);
  if (exactChild !== undefined) {
    best = findBestMatch(exactChild, tokens, index + 1, exactTokens + 1, patternIndex, best);
  }
  if (node.star !== null) {
    best = findBestMatch(node.star, tokens, index + 1, exactTokens, patternIndex, best);
  }
  return best;
}

function takeMoreSpecific(current: MatchCandidate | null, next: MatchCandidate): MatchCandidate {
  if (current === null) return next;
  if (next.tier < current.tier) return next;
  if (next.tier > current.tier) return current;
  // Same tier — prefer the one with more exact tokens (tie-breaker).
  if (next.exactTokens > current.exactTokens) return next;
  return current;
}

/**
 * Returns the pre-existing Subject for `pattern` if it is already registered,
 * or `null` if not. Used by `register()` to detect specificity collisions.
 */
function lookupExistingTerminator(root: TrieNode, pattern: string): Subject | null {
  const tokens = pattern.split('.');
  let node: TrieNode = root;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token === undefined) return null;
    const isLast = i === tokens.length - 1;
    if (token === '>') return node.rest;
    if (token === '*') {
      if (node.star === null) return null;
      node = node.star;
    } else {
      const child = node.exact.get(token);
      if (child === undefined) return null;
      node = child;
    }
    if (isLast) return node.subject;
  }
  return null;
}

export interface SubjectTrie {
  /**
   * Insert `pattern` and attach `observer` to its terminator. Returns an
   * unregister fn that removes the observer (and, if last, decrements
   * interestCount). Throws if `pattern` is already registered with an
   * observer set whose specificity exactly collides with another already-
   * registered pattern at the same node.
   */
  register(pattern: string, observer: Observer): () => void;
  /** Walk every Subject matching `literal`. */
  walk(literal: string, visit: SubjectVisitor): void;
  /** Most-specific registered pattern that matches `literal`, or null. */
  matchPattern(literal: string): string | null;
  /** Pure helper — does `pattern` match `literal`? */
  subjectMatches(pattern: string, literal: string): boolean;
}

export function createSubjectTrie(): SubjectTrie {
  const root: TrieNode = createTrieNode();
  const patternIndex: PatternIndex = new Map();
  const matchCache = new Map<string, string | null>();

  function register(pattern: string, observer: Observer): () => void {
    matchCache.clear();
    const existing = lookupExistingTerminator(root, pattern);
    const subject = insertPattern(root, pattern);
    // Specificity-collision guard: if the terminator already had a
    // different canonical pattern, the caller is registering a duplicate
    // at the same trie position with a conflicting string. The trie node
    // is shared, but the canonical pattern must be unique per Subject.
    const priorPattern = patternIndex.get(subject);
    if (priorPattern !== undefined && priorPattern !== pattern) {
      throw new Error(
        `subject-trie · register(): pattern '${pattern}' collides with ` +
          `already-registered pattern '${priorPattern}' at the same trie ` +
          `terminator (same specificity). Each terminator must have exactly ` +
          `one canonical pattern string.`,
      );
    }
    if (priorPattern === undefined) {
      patternIndex.set(subject, pattern);
    }
    void existing; // existing is only used as a no-op anchor for tests.
    subject.interestCount += 1;
    subject.observers.add(observer);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      subject.observers.delete(observer);
      subject.interestCount = Math.max(0, subject.interestCount - 1);
    };
  }

  function walkFn(literal: string, visit: SubjectVisitor): void {
    const tokens = literal.split('.');
    walk(root, tokens, 0, visit);
  }

  function matchPattern(literal: string): string | null {
    const cached = matchCache.get(literal);
    if (cached !== undefined || matchCache.has(literal)) return cached ?? null;
    const tokens = literal.split('.');
    const best = findBestMatch(root, tokens, 0, 0, patternIndex, null);
    const pattern = best === null ? null : best.pattern;
    if (matchCache.size >= 1024) matchCache.clear();
    matchCache.set(literal, pattern);
    return pattern;
  }

  return {
    register,
    walk: walkFn,
    matchPattern,
    subjectMatches,
  };
}
